import type {
  ChangelogI18N,
  ChangelogI18NBlock,
  ChangelogItemStaff,
  ChangelogReleaseListItem,
} from '@/api'
import type { ChangelogFeature, ChangelogFix, Release } from '../types'

export type ChangelogLocale = 'fr' | 'en' | 'de'

const formatFeatureId = (index: number) =>
  `Ft${String(index + 1).padStart(2, '0')}`

const formatFixId = (index: number) =>
  `Fix${String(index + 1).padStart(2, '0')}`

const pickI18nBlock = (
  i18n: ChangelogI18N | undefined,
  locale: ChangelogLocale
): ChangelogI18NBlock => i18n?.[locale] ?? i18n?.fr ?? i18n?.en ?? {}

const mapItemsFromStaff = (
  items: ChangelogItemStaff[],
  locale: ChangelogLocale
): { features: ChangelogFeature[]; fixes: ChangelogFix[] } => {
  const features: ChangelogFeature[] = []
  const fixes: ChangelogFix[] = []

  items.forEach((item) => {
    const block = pickI18nBlock(item.i18n, locale)
    if (item.kind === 'FEATURE') {
      features.push({
        id: formatFeatureId(features.length),
        title: block.title ?? '',
        description: block.description,
      })
    } else if (item.kind === 'FIX') {
      fixes.push({
        id: formatFixId(fixes.length),
        title: block.title ?? '',
        description: block.description,
      })
    }
  })

  return { features, fixes }
}

/** Mappe une release API (shape publique ou staff) vers le modèle UI. */
export const mapApiReleaseToUi = (
  item: ChangelogReleaseListItem,
  locale: ChangelogLocale
): Release => {
  const releaseId = item.releaseId

  const hasPublicShape =
    item.title != null ||
    item.description != null ||
    (item.features?.length ?? 0) > 0 ||
    (item.fixes?.length ?? 0) > 0

  if (hasPublicShape) {
    return {
      releaseId,
      version: item.version,
      date: item.date ?? item.releaseDate ?? '',
      title: item.title ?? '',
      summary: item.description,
      features: (item.features ?? []).map((f, i) => ({
        id: formatFeatureId(i),
        title: f.title,
        description: f.description,
      })),
      fixes: (item.fixes ?? []).map((f, i) => ({
        id: formatFixId(i),
        title: f.title,
        description: f.description,
      })),
    }
  }

  const releaseBlock = pickI18nBlock(item.i18n, locale)
  const { features, fixes } = mapItemsFromStaff(item.items ?? [], locale)

  return {
    releaseId,
    version: item.version,
    date: item.releaseDate ?? item.date ?? '',
    title: releaseBlock.title ?? '',
    summary: releaseBlock.description,
    features,
    fixes,
  }
}
